import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import './app/fonts';
import './styles/globals.css';
import { AppProviders, queryClient } from '@app/providers';
import { router } from '@app/router';
import { bootstrapSession } from '@features/auth/model/session';
import { useStepUp } from '@features/auth/model/stepUp';
import { notificationKeys } from '@features/notifications/api';
import { startWebPush } from '@features/notifications/push/register';
import { configureApiHandlers } from '@shared/api';
import { applyDocumentLocale, initI18n } from '@shared/lib/i18n/i18n';
import { readDeviceLocale, resolveLocale } from '@shared/lib/i18n/locales';
import { applyTheme, usePreferences } from '@shared/model/preferences';
import { sessionStore } from '@shared/session/store';

async function start() {
  // Before first paint: theme, then locale (device choice → browser → en; the profile wins after refresh).
  applyTheme(usePreferences.getState().theme);
  const locale = resolveLocale({ device: readDeviceLocale(), browser: navigator.languages });
  applyDocumentLocale(locale);
  await initI18n(locale);

  configureApiHandlers({
    onAuthLost: () => {
      const wasSignedIn = sessionStore.get().status === 'authenticated';
      sessionStore.get().clear();
      queryClient.clear();
      if (wasSignedIn) {
        void router.navigate({ to: '/login', search: { redirect: router.state.location.href } });
      }
    },
    onStepUpRequired: () => useStepUp.getState().request(),
  });

  // Web push: service worker + subscription kept in step with the signed-in account (dev and production).
  startWebPush({
    navigate: (url) => void router.navigate({ href: url }),
    onPushReceived: () => void queryClient.invalidateQueries({ queryKey: notificationKeys.all }),
  });

  // Restore the session silently (httpOnly refresh cookie → in-memory access token).
  void bootstrapSession();

  const root = document.getElementById('root');
  if (!root) throw new Error('#root missing');
  createRoot(root).render(
    <StrictMode>
      <AppProviders>
        <RouterProvider router={router} />
      </AppProviders>
    </StrictMode>,
  );
}

void start();
